// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss, xClassNames, xScreen } from "@keller/XDL/Utilities";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    customCSS = "customCSS",
    // XNG-Changes
    imageWidth = "imageWidth",
    imageHeight = "imageHeight",
    alignment = "alignment",
    // XNG-Changes: For text wrap arount the image
    enableWrap = "enableWrap",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.alignment]: string;
        [fields.imageWidth]: string;
        [fields.imageHeight]: string;
        [fields.enableWrap]: boolean;
    };
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
}

export const imageStyles: ImageStyles = {
    image: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
    img: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
};

export const Image: FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.imageLink);
    let styles = useMergeStyles("image", imageStyles);

    styles = mergeWithCss(styles, {
        wrapper: {
            css: css`
                display: flex;
                ${fields.alignment &&
                css`
                    justify-content: ${fields.alignment};
                `};
            `,
        },
        img: {
            css: css`
                ${fields.imageHeight &&
                css`
                    height: ${fields.imageHeight};
                `}
                ${fields.imageWidth &&
                css`
                    width: ${fields.imageWidth};
                `}
            `,
        },
    } as ImageStyles);

    const image = <Img className="img" src={fields.imageUrl} altText={fields.altText} loading="lazy" {...styles.img} />;

    return (
        <StyledWrapper {...styles.wrapper} className={xClassNames.append([fields.enableWrap && "wrap-text-image"])}>
            {url ? (
                <Link href={url} {...styles.imageLink}>
                    {image}
                </Link>
            ) : (
                image
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: Image,
    definition: {
        group: "Basic",
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.imageUrl,
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.imageLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.alignment,
                displayName: "Alignment",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    {
                        displayName: "Default",
                        value: "initial",
                    },
                    {
                        displayName: "Flex Start",
                        value: "flex-start",
                    },
                    {
                        displayName: "Flex End",
                        value: "flex-end",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                ],
                defaultValue: "initial",
            },
            {
                name: fields.imageWidth,
                displayName: "Image Width",
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                placeholder: "Enter: 200px or any width style",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("width", value) ? null : `Invalid Input`;
                },
            },
            {
                name: fields.imageHeight,
                displayName: "Image Height",
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                placeholder: "Enter: 50px or any height style",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("height", value) ? null : `Invalid Input`;
                },
            },
            {
                name: fields.enableWrap,
                displayName: "Enable texts to wrapping",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
